import { ContentLabel, ContentLabelAction } from '@/components/ContentLabel'
import { Link } from '@/components/Link'
import { links } from '@/utils/link'
import type { ReactNode } from 'react'
import {
  ContentLayout,
  ContentMain,
  ContentTip,
} from '@/components/ContentLayout'
import { FormSection } from '@/components/Form'
import styles from './General.module.css'
import { SwitchCheckbox } from '@/components/SwitchCheckbox'
import { byDesign } from '@/infra/design'
import imgWorkspaceSettingsUrl from '@/assets/misc/img_workspace-settings.svg'
import PortalOverviewProfileIllustration from '@/components/SpotIllustration/PortalOverviewProfileIllustration'

export interface GeneralProps {
  header: ReactNode
  corporateMode: boolean
  /**
   * The loading state, or `GeneralSections` once loaded.
   */
  children: ReactNode
}

export function General({ header, corporateMode, children }: GeneralProps) {
  return (
    <div className={styles.root}>
      {header}
      <ContentLayout accountForLabel>
        <ContentMain>{children}</ContentMain>
        <ContentTip
          corporateMode={corporateMode}
          title="Profile and visibility"
          illustrationUrl={imgWorkspaceSettingsUrl}
          illustration={byDesign(undefined, PortalOverviewProfileIllustration)}
        >
          Manage your personal information, and control which information other
          people see and apps may access.
        </ContentTip>
      </ContentLayout>
      <footer className={styles.footer}>
        <Link to={links.about()} className={styles.about}>
          About Taxfyle
        </Link>
      </footer>
    </div>
  )
}

export interface GeneralSectionsProps {
  userInfo: ReactNode
  isRefreshDesign: boolean
  onDesignToggle: () => unknown
  isPro: boolean
  proDetails?: ReactNode
  canViewAndEditOwnSkills: boolean
  onManageSkills: () => unknown
  skills?: ReactNode
  canViewAndEditOwnRoles: boolean
  onManageRoles: () => unknown
  roles?: ReactNode
}

export function GeneralSections({
  userInfo,
  isRefreshDesign,
  onDesignToggle,
  isPro,
  proDetails,
  canViewAndEditOwnSkills,
  onManageSkills,
  skills,
  canViewAndEditOwnRoles,
  onManageRoles,
  roles,
}: GeneralSectionsProps) {
  return (
    <>
      <FormSection label="General">{userInfo}</FormSection>
      <FormSection label="Experimental">
        <DesignSetting
          isRefreshDesign={isRefreshDesign}
          onToggle={onDesignToggle}
        />
      </FormSection>
      {isPro && <FormSection label="Pro Details">{proDetails}</FormSection>}
      <div className={styles.columns}>
        {canViewAndEditOwnSkills && (
          <div className={styles.column}>
            <FormSection
              edgeToEdge
              label={
                <SectionLabelWithEdit
                  editTitle="Manage"
                  onEdit={onManageSkills}
                >
                  Skills
                </SectionLabelWithEdit>
              }
            >
              {skills}
            </FormSection>
          </div>
        )}
        {canViewAndEditOwnRoles && (
          <div className={styles.column}>
            <FormSection
              edgeToEdge
              label={
                <SectionLabelWithEdit editTitle="Manage" onEdit={onManageRoles}>
                  Roles
                </SectionLabelWithEdit>
              }
            >
              {roles}
            </FormSection>
          </div>
        )}
      </div>
    </>
  )
}

interface DesignSettingProps {
  isRefreshDesign: boolean
  onToggle: () => unknown
}

function DesignSetting({ isRefreshDesign, onToggle }: DesignSettingProps) {
  return (
    <div className={styles.setting}>
      <div>
        <p className={styles.settingTitle}>New design</p>
        <p className={styles.settingDescription}>
          Try the redesigned portal in this browser. The page reloads to apply
          it.
        </p>
      </div>
      <SwitchCheckbox
        noText
        value={isRefreshDesign}
        onChange={onToggle}
        testId="profile-new-design-switch"
      />
    </div>
  )
}

interface SectionLabelWithEditProps {
  onEdit?(): unknown
  editTitle: string
  children: ReactNode
}

function SectionLabelWithEdit({
  onEdit,
  editTitle,
  children,
}: SectionLabelWithEditProps) {
  return (
    <ContentLabel
      block
      actions={
        onEdit && (
          <ContentLabelAction onPress={onEdit}>{editTitle}</ContentLabelAction>
        )
      }
    >
      {children}
    </ContentLabel>
  )
}
